import {
  walls,
  Model,
  allColors,
  Snake,
  DyingSnake,
  foodWidth,
} from '../server-src/model'
import { getStuff } from './SnakeParts'

export interface Camera {
  width: number
  height: number
  x: number
  y: number
  showAll: boolean
}

function minmax(a: number, min: number, max: number) {
  if (max < min) {
    return (min + max) / 2
  }
  return Math.min(Math.max(a, min), max)
}

const isSmall = window.matchMedia
  ? window.matchMedia('screen and (max-width: 900px)').matches
  : screen.width <= 480
let sideways =
  window.matchMedia && window.matchMedia('(orientation: landscape)').matches
window.addEventListener('orientationchange', () => {
  sideways =
    window.matchMedia && window.matchMedia('(orientation: landscape)').matches
})
const maxSize = (showAll: boolean) =>
  showAll ? Infinity : isSmall ? (sideways ? 60 : 30) : 90
function getScale({
  width,
  height,
  showAll,
}: {
  width: number
  height: number
  showAll: boolean
}) {
  return (
    width /
    Math.min(maxSize(showAll), Math.max(walls.x, (walls.y / height) * width))
  )
}
function getCameraGameDimensions(camera: Camera) {
  return [camera.width / getScale(camera), camera.height / getScale(camera)]
}
function getTranslation(camera: Camera) {
  const [wid, hei] = getCameraGameDimensions(camera)
  return [
    minmax(camera.x - wid / 2, 0, walls.x - wid),
    minmax(camera.y - hei / 2, 0, walls.y - hei),
  ]
}
function transformDist(
  dist: number,
  camera: { width: number; height: number; showAll: boolean }
) {
  return dist * getScale(camera)
}
function transformCoord(x: number, y: number, camera: Camera) {
  const [xTranslate, yTranslate] = getTranslation(camera)
  return [
    transformDist(x - xTranslate, camera),
    transformDist(y - yTranslate, camera),
  ]
}

function rect(
  ctx: CanvasRenderingContext2D,
  sizing: Camera,
  x: number,
  y: number,
  width: number,
  height: number
) {
  const transformed = transformCoord(x, y, sizing)
  const transformedWidth = transformDist(width, sizing)
  const transformedHeight = transformDist(height, sizing)
  if (
    /* on screen */
    transformed[0] + transformedWidth >= 0 &&
    transformed[1] + transformedHeight >= 0 &&
    transformed[0] < sizing.width &&
    transformed[1] < sizing.height
  )
    ctx.fillRect(
      transformed[0],
      transformed[1],
      transformedWidth,
      transformedHeight
    )
}

function drawLines(ctx: CanvasRenderingContext2D, sizing: Camera) {
  ctx.save()
  ctx.lineWidth = 0.3
  ctx.beginPath()
  let [initialX, initialY] = transformCoord(0, 0, sizing)
  const space = transformDist(50, sizing)

  const incrementsX = Math.floor((0 - initialX) / space) + 1
  initialX += incrementsX * space
  for (let x = initialX; x < document.body.clientWidth; x += space) {
    ctx.moveTo(x, 0)
    ctx.lineTo(x, document.body.clientHeight)
    ctx.stroke()
  }

  const incrementsY = Math.floor((0 - initialY) / space) + 1
  initialY += incrementsY * space
  for (let y = initialY; y < document.body.clientHeight; y += space) {
    ctx.moveTo(0, y)
    ctx.lineTo(document.body.clientWidth, y)
    ctx.stroke()
  }
  ctx.restore()
}

// 2(i-1) is horizontal
// 2(i-1) + 1 is vertical
const offscreenCanvases: { [k: string]: HTMLCanvasElement[] } = {}
const MAX_CANVASES = 25
export function generateCanvases(
  width: number,
  height: number,
  showAll: boolean
) {
  performance.mark('generateCanvases-start')
  for (const color of allColors) {
    const canv = document.createElement('canvas')
    const one = transformDist(1, { width, height, showAll })
    canv.width = one * 1.1
    canv.height = one * 1.1
    const ctx = canv.getContext('2d')!
    ctx.fillRect(0, 0, one, one)
    ctx.fillStyle = color ?? 'green'
    ctx.fillRect(one * 0.2, one * 0.2, one * 0.6, one * 0.6)
    offscreenCanvases[color] = []
    for (let i = 1; i < MAX_CANVASES; i++) {
      const newVertCanv = document.createElement('canvas')
      newVertCanv.width = one * 1.1
      newVertCanv.height = one * i * 1.1
      const vertCtx = newVertCanv.getContext('2d')
      const newHorizCanv = document.createElement('canvas')
      newHorizCanv.width = one * i * 1.1
      newHorizCanv.height = one * 1.1
      const horizCtx = newHorizCanv.getContext('2d')
      for (let j = 0; j < i; j++) {
        vertCtx?.drawImage(canv, 0, one * j)
        horizCtx?.drawImage(canv, one * j, 0)
      }
      offscreenCanvases[color][2 * (i - 1)] = newHorizCanv
      offscreenCanvases[color][2 * (i - 1) + 1] = newVertCanv
    }
  }
  console.log(performance.measure('generateCanvases', 'generateCanvases-start'))
}

function renderSnake(
  snake: Snake | DyingSnake,
  ctx: CanvasRenderingContext2D,
  sizing: Camera
) {
  if ('alpha' in snake) {
    ctx.save()
    ctx.globalAlpha = snake.alpha
  }
  const stuff = getStuff(
    snake.jointLengths,
    snake.head,
    snake.initialDirectionVertical,
    snake.length
  )
  for (let [[x, y], len, isVertical] of stuff) {
    while (len > 0) {
      const nextLen = Math.min(len, MAX_CANVASES - 1)
      const [transformedX, transformedY] = transformCoord(x, y, sizing)
      const w = transformDist(isVertical ? 1 : nextLen, sizing)
      const h = transformDist(isVertical ? nextLen : 1, sizing)
      if (
        transformedX <= sizing.width &&
        transformedY <= sizing.height &&
        transformedX + w >= 0 &&
        transformedY + h >= 0
      ) {
        ctx.drawImage(
          offscreenCanvases[snake.color][
            2 * (nextLen - 1) + (isVertical ? 1 : 0)
          ],
          transformedX,
          transformedY
        )
      }
      len -= nextLen
      if (isVertical) y += nextLen
      else x += nextLen
    }
  }
  if ('alpha' in snake) {
    ctx.restore()
  }
}

// let cancelId: number | undefined
export function rerender(
  ctx: CanvasRenderingContext2D,
  model: Model,
  sizing: Camera
) {
  ctx.clearRect(0, 0, sizing.width, sizing.height)
  drawLines(ctx, sizing)
  for (const snake of model.snakes) {
    renderSnake(snake, ctx, sizing)
  }
  for (const snake of model.dyingSnakes) {
    renderSnake(snake, ctx, sizing)
  }

  ctx.save()
  ctx.fillStyle = 'blue'
  for (const [x, y] of model.food) {
    rect(ctx, sizing, x, y, foodWidth, foodWidth)
  }
  ctx.restore()

  // walls
  ctx.save()
  ctx.fillStyle = 'brown'
  rect(ctx, sizing, -0.5, -0.5, 1, walls.y + 1) // left
  rect(ctx, sizing, 0, -0.5, walls.x, 1) // top
  rect(ctx, sizing, walls.x - 0.5, -0.5, 1, walls.y + 1) // right
  rect(ctx, sizing, 0, walls.y - 0.5, walls.x, 1) // bottom
  ctx.restore()

  //announcement
  if (model.announcement != null) {
    ctx.save()
    ctx.textAlign = 'center'
    ctx.font = isSmall ? '20px sans-serif' : '40px sans-serif'
    ctx.fillText(model.announcement, sizing.width / 2, 50)
    ctx.restore()
  }
}
